import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Sentry 이벤트의 `contexts.axios` 블록을 만든다.
 *
 * 동기: ZEP-394 이후 axios 에러 메시지에 `[METHOD URL STATUS]`가 박혔지만,
 * 디버깅에는 요청 payload / 응답 body가 필수다. 그게 없으니 500 케이스
 * (예: QUIZ-APP-70V "Internal Server Error")는 Sentry 화면만 보고는 무엇이
 * 잘못 갔는지 판별 불가했다. 이 모듈이 그 갭을 메운다.
 *
 * 입력 후보 (코드베이스 내 실재 형태):
 *  - 그대로의 `AxiosError` (admin 등): config/response 보유
 *  - `annotateAxiosError`로 가공된 AxiosError (play-app/web-app): + httpStatus/status
 *  - `ZepQuizApiError` (quiz-app): config/response 없을 수 있고 `details = response.data` 보유
 *  - `ZepApiError` (play-app/web-app): backend status<0 응답을 wrapping
 */

const SENSITIVE_KEY_RE =
  /(password|passwd|token|secret|authorization|api[-_]?key|cvc|cvv|pin|cardnumber)/i;

// 객체 키 순회로는 못 잡는 form-urlencoded(`password=xxx&token=yyy`) 평문 케이스를 위해
// 문자열 원문에도 마스킹을 적용한다. JSON 파싱이 실패한 string body / 외부에서 들어온
// query-string 형태에 모두 통한다.
const SENSITIVE_FORM_RE =
  /(password|passwd|token|secret|authorization|api[-_]?key|cvc|cvv|pin|cardnumber)=([^&\s]*)/gi;

function scrubSensitiveString(value: string): string {
  return value.replace(SENSITIVE_FORM_RE, '$1=[Scrubbed]');
}

/** 컨텍스트 항목당 사이즈 캡 — Sentry 이벤트 폭발 방지 */
const MAX_FIELD_BYTES = 4_000;

/** 객체 깊이 한도 — 순환 참조/거대 트리 방지 */
const MAX_DEPTH = 4;

/** 배열 항목 한도 */
const MAX_ARRAY_ITEMS = 50;

export function scrubSensitive(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return '[Truncated]';
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map(item => scrubSensitive(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, v]) =>
      SENSITIVE_KEY_RE.test(key)
        ? [key, '[Scrubbed]']
        : [key, scrubSensitive(v, depth + 1)],
    ),
  );
}

export function safeJsonStringify(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  try {
    let scrubbed = scrubSensitive(value);
    // 거대 문자열(수백 KB HTML/로그 본문 등)을 통째로 stringify하면 동기 직렬화 비용이
    // beforeSend 경로에서 그대로 발생한다. scrub 결과가 문자열이면 stringify 전에 컷.
    if (typeof scrubbed === 'string' && scrubbed.length > MAX_FIELD_BYTES) {
      scrubbed = `${scrubSensitiveString(
        scrubbed.slice(0, MAX_FIELD_BYTES),
      )}…(truncated)`;
    } else if (typeof scrubbed === 'string') {
      scrubbed = scrubSensitiveString(scrubbed);
    }
    const json = JSON.stringify(scrubbed);
    if (json == null) return undefined;
    return json.length > MAX_FIELD_BYTES
      ? `${json.slice(0, MAX_FIELD_BYTES)}…(truncated)`
      : json;
  } catch {
    return '[Unserializable]';
  }
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function pickTraceId(
  headers: Record<string, unknown> | undefined,
): string | undefined {
  if (!headers) return undefined;
  const candidate =
    headers['x-zep-trace-id'] ??
    headers['X-ZEP-Trace-Id'] ??
    headers['x-request-id'] ??
    headers['X-Request-Id'];
  return typeof candidate === 'string' ? candidate : undefined;
}

interface AxiosErrorLike {
  config?: {
    method?: string;
    url?: string;
    baseURL?: string;
    params?: unknown;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
}

interface ZepQuizApiErrorLike {
  details?: unknown;
}

export interface AxiosContext {
  method?: string;
  url?: string;
  baseURL?: string;
  params?: string;
  requestBody?: string;
  responseStatus?: number;
  responseBody?: string;
  requestId?: string;
  // Sentry의 Context 타입(`Record<string, unknown>`)에 호환되도록 index signature를 둔다.
  [key: string]: unknown;
}

/**
 * 에러 객체에서 axios 컨텍스트를 추출한다.
 * 하나라도 의미 있는 필드를 채우지 못하면 null 반환.
 */
export function buildAxiosContext(error: unknown): AxiosContext | null {
  if (error == null || typeof error !== 'object') return null;

  const axiosLike = error as AxiosErrorLike;
  const zepQuiz = error as ZepQuizApiErrorLike;
  const ctx: AxiosContext = {};

  if (axiosLike.config) {
    // buildAxiosContext는 axios가 아닌 임의 originalException에도 호출되므로
    // method가 string이 아닌 경우(숫자/객체) toUpperCase가 throw → beforeSend 자체가
    // 실패해 이벤트가 드랍될 수 있다. 타입 가드 필수.
    if (typeof axiosLike.config.method === 'string') {
      ctx.method = axiosLike.config.method.toUpperCase();
    }
    if (axiosLike.config.url) ctx.url = axiosLike.config.url;
    if (axiosLike.config.baseURL) ctx.baseURL = axiosLike.config.baseURL;
    if (axiosLike.config.params != null) {
      ctx.params = safeJsonStringify(axiosLike.config.params);
    }
    if (axiosLike.config.data != null) {
      const data =
        typeof axiosLike.config.data === 'string'
          ? safeParseJson(axiosLike.config.data)
          : axiosLike.config.data;
      ctx.requestBody = safeJsonStringify(data);
    }
  }

  if (axiosLike.response) {
    if (typeof axiosLike.response.status === 'number') {
      ctx.responseStatus = axiosLike.response.status;
    }
    if (axiosLike.response.data != null) {
      ctx.responseBody = safeJsonStringify(axiosLike.response.data);
    }
  }

  // X-ZEP-Trace-Id는 ClickHouse otel_traces의 SpanAttributes['http.x_trace_id']와 매칭되는
  // correlation 키. Sentry → ClickHouse 점프의 핵심이라 응답 헤더 우선, 없으면(네트워크
  // 에러로 응답 자체가 없는 경우) request 헤더에서 fallback으로 잡는다.
  const reqId =
    pickTraceId(axiosLike.response?.headers) ??
    pickTraceId(axiosLike.config?.headers);
  if (reqId) ctx.requestId = reqId;

  // ZepQuizApiError는 axios.config/response 없이 details만 들고 throw 되는 경우가 있다.
  if (ctx.responseBody == null && zepQuiz.details != null) {
    ctx.responseBody = safeJsonStringify(zepQuiz.details);
  }

  return Object.keys(ctx).length > 0 ? ctx : null;
}

/** event.contexts.axios에 합쳐 넣는다. 이미 다른 contexts가 있어도 보존. */
export function attachAxiosContext(event: ErrorEvent, error: unknown): void {
  // beforeSend 경로의 어떤 예외도 이벤트 자체를 드랍시키면 안 된다(컨텍스트 부착은
  // best-effort, 실패해도 원래 에러는 Sentry로 가야 한다).
  try {
    const ctx = buildAxiosContext(error);
    if (!ctx) return;
    event.contexts = { ...event.contexts, axios: ctx };
  } catch {
    // swallow: context 부착 실패가 이벤트 손실로 이어지면 안 됨
  }
}
